/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import { ApiContext } from '../../../../../context/ApiContext';
import { ConversionType } from '../../../../../main';
import {
  euiDefaultUnit,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../../shared/Utils/Constants';
import { getProgressBarUnitScaledData } from '../../../../shared/Utils/portfolioDataFormatter';
import {
  getSpanDetails,
  getPerformanceGraphData,
  getPerformanceGraphDataNew
} from '../../../../shared/Utils/Utils';
import { IEmaasEnergyUsageComponentData } from '../../../IEnergyManagementProps';
import {
  convertDataforPortfolioBreakdownByMeter,
  convertDataforPortfolioEnergyBreakdownBySite,
  getNullToZero,
  headersEnergyUsageBasic
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { IMeterDataProps } from '../../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import {
  getEnergyEndDate,
  getToUnitForChart
} from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { getEuiUnitDisplayName } from '../../../../shared/Utils/unitHandling/EuiUnitHandling';
import CommonEnergyComponent from '../../../../shared/EnergyKPI/CommonEnergyComponent';

interface IEmaasEnergyUsageComponentProps {
  data: IEmaasEnergyUsageComponentData;
  viewType?: string;
  type?: string;
  optimusSubscribed?: boolean;
  checkAllSitesAndMonthData?: boolean;
  dataSourceType?: string;
}

export const PortfolioEnergyCard = (props: IEmaasEnergyUsageComponentProps) => {
  const [t] = useTranslation();
  let { data, viewType, optimusSubscribed, checkAllSitesAndMonthData } = props;
  const apiContext = useContext(ApiContext);
  const { redirect, preferredEuiUnit, userPreferedUnit } = apiContext;

  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  fromUnit = userPreferedUnit as any;

  let {
    summary: {
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      absoluteTotalBaselineConsumption,
      absoluteTotalConsumption
    },
    breakdownBySite,
    totalSiteCount,
    aggregationsStartDate,
    aggregationsEndDate,
    performanceAggregationApplied,
    performanceAggregation,
    energyMeasurementUnit,
    euiEnergyMeasuringUnit,
    rootNodeId
  } = data;

  const onViewDetailsClick = () => {
    if (redirect)
      redirect(`/energyViewDetails/root/${rootNodeId}/portfolio-view`);
  };
  const dateData = getSpanDetails(aggregationsStartDate, aggregationsEndDate);

  const consumptionBreakdownMeterData = convertDataforPortfolioEnergyBreakdownBySite(
    breakdownBySite,
    fromUnit, //energyMeasurementUnit,
    euiEnergyMeasuringUnit || euiDefaultUnit,
    preferredEuiUnit || '',
    undefined,
    energyMeasurementUnit,
    apiContext.showChangeWhenData
  );
  const { values } = consumptionBreakdownMeterData;

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    ConversionType.ELECTRICITY,
    { previousPeriod: totalBaselineConsumption, actual: totalConsumption },
    totalEnergyDeviation,
    fromUnit, //energyMeasurementUnit,
    undefined,
    absoluteTotalBaselineConsumption,
    absoluteTotalConsumption,
    energyMeasurementUnit,
    apiContext.showChangeWhenData,
    true
  );
  const ConvertToUnit = getToUnitForChart(
    performanceAggregation,
    ConversionType.ELECTRICITY,
    fromUnit //energyMeasurementUnit
  );

  const comparisonData =
    performanceAggregationApplied === 'Hourly'
      ? getPerformanceGraphData(
          performanceAggregation,
          energyMeasurementUnit,
          ConvertToUnit,
          performanceAggregationApplied,
          ConversionType.ELECTRICITY
          // aggregationsStartDate,
          // aggregationsEndDate
        )
      : getPerformanceGraphDataNew(
          performanceAggregation,
          energyMeasurementUnit,
          ConvertToUnit,
          performanceAggregationApplied,
          ConversionType.ELECTRICITY,
          aggregationsStartDate,
          aggregationsEndDate
        );

  const percentSavingsForProgressBar = parseFloat(
    getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
      KPI_PERCENTAGE_DECIMAL_PRECISION
    )
  );

  const savingPercent =
    apiContext.showChangeWhenData &&
    progressBarUnitScaledData.deviation !== null &&
    (isNaN(Number(progressBarUnitScaledData.actual)) ||
      isNaN(Number(progressBarUnitScaledData.previousPeriod)) ||
      Number(progressBarUnitScaledData.actual) === 0 ||
      Number(progressBarUnitScaledData.previousPeriod) === 0 ||
      Math.abs(Number(percentSavingsForProgressBar)) >
        PERCENT_CHANGE_THRESHOLD ||
      !checkAllSitesAndMonthData);

  const aggregationEnergyData = {
    actual: progressBarUnitScaledData.actual,
    previousPeriod: progressBarUnitScaledData.previousPeriod,
    absoluteActual: progressBarUnitScaledData.absoluteActual,
    absolutePrevPeriod: progressBarUnitScaledData.absolutePreviousPeriod,
    actualUnit: progressBarUnitScaledData.actualUnit,
    baselineUnit: progressBarUnitScaledData.baselineUnit,
    changeUnit: progressBarUnitScaledData.changeUnit,
    savings: savingPercent ? null : progressBarUnitScaledData.deviation,
    savingsPercentage: savingPercent
      ? null
      : parseFloat(
          getNullToZero(totalEnergyDeviationPercentage)?.toFixed(
            KPI_PERCENTAGE_DECIMAL_PRECISION
          )
        )
  };

  const convertedEuiUnit = getEuiUnitDisplayName(preferredEuiUnit);
  const energyComponentProps = {
    onViewDetailsClick: onViewDetailsClick,
    progressBarData: {
      aggregationData: aggregationEnergyData,
      unit: progressBarUnitScaledData.ConvertedUnit,
      deviationScaledData: progressBarUnitScaledData.deviationScaledData
    },
    graphData: {
      chartSeries: comparisonData,
      startTime: aggregationsStartDate,
      endTime: aggregationsEndDate,
      aggregationType: performanceAggregationApplied
    },
    tableData: {
      columnHeaders: headersEnergyUsageBasic(
        t,
        viewType,
        totalSiteCount,
        '',
        undefined,
        convertedEuiUnit
      ),
      meterData: (values as unknown) as IMeterDataProps[]
    },
    dateData: dateData,
    type: props.type,
    energyMeasurementUnit: energyMeasurementUnit,
    fromCarbonKPI: true,
    originalUnit: energyMeasurementUnit,
    conversionType: ConversionType.ELECTRICITY,
    optimusSubscribed: optimusSubscribed as boolean,
    dataSourceType: props.dataSourceType
  };
  return <CommonEnergyComponent {...energyComponentProps} />;
};
export default memo(PortfolioEnergyCard);
